import React, {Component} from "react"
import PropTypes from "prop-types"
import withAuth, {WithAuth} from "@irati/auth/helpers/auth/withAuth"
import {Wallet, DealCategory, DealCategoryType, FlexiPointsExpiry, PaymentCurrencyType} from "@irati/types"
import {QueryFlexiPointsExpiryListArgs} from "@irati/types/generated/ops"
import {getNormalisedValue} from "@irati/utils/currency/formatCurrency"
import authentication from "@irati/auth"
import {Label, H2, H3, H4, Body} from "@irati/ui/src/Typography"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import categoriesService from "@irati/deals/services/dealCategory"
import {walletTracking} from "@irati-tracking/wallet"
import {FormattedMessage, FormattedCurrency} from "@irati/utils/lang"
import id from "@irati/utils/id"
import flexiPointService from "@irati/wallet/services/flexiRewards"
import {Tooltip} from "@irati/ui/src/Tooltip"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Flex from "@irati/ui/src/Flex"

import {hangPromise} from "@irati/utils/promise"
import mainTheme from "@irati/ui/src/global/theme"

import TreatsTriggerModal from "../TreatsModal/TreatsTriggerModal"
import ExpiringSoonTooltipContent from "../ExpiringSoonTooltipContent"
import {Trigger, ActiveCard} from "./styled"

interface Props extends WithAuth {
  wallet: Wallet,
  active: boolean
}

interface State {
  loadCategories: Promise<any>,
  ttCategory?: DealCategory | null,
  expiredFlexiPoints?: FlexiPointsExpiry | null
}

const date = new Date()
const now = date.toISOString()
const ThirtyDaysFromNow = new Date(date.setDate(date.getDate() + 30)).toISOString()

class TreatTokenWallet extends Component<Props, State> {
  static contextTypes = {
    apps: PropTypes.object.isRequired
  }

  state: State = {
    ttCategory: null,
    loadCategories: hangPromise(),
    expiredFlexiPoints: null
  }

  componentDidMount() {
    const variables: QueryFlexiPointsExpiryListArgs = {
      walletId: this.props.wallet.uuid!,
      currency: PaymentCurrencyType.Tt,
      fromDate: now,
      beforeDate: ThirtyDaysFromNow // now + 30 days
    }

    if(this.props.wallet.uuid) {
      flexiPointService.flexiPointsExpiryList(variables).then((expiryList) => {
        this.setState({expiredFlexiPoints: expiryList})
      })
    }

    const loadCategories = categoriesService.listForCurrentPackage().then((dealCategories) => {
      this.setState({
        ttCategory: dealCategories.find((category) => category.type === DealCategoryType.Tt)
      })
    })

    this.setState({loadCategories})
  }

  handleCardClick = () => {
    walletTracking.clickTreatTokenCard(this.props.auth.userTenant.uuid)
  }

  render() {
    const {wallet, active} = this.props
    const {availableBalance, currency} = wallet
    const {ttCategory, loadCategories, expiredFlexiPoints} = this.state
    const {apps} = this.context
    const idNamespace = id("balances", this.props)
    const isWellnessHubActive = authentication.apps("wellnessHub")

    return (
      <ActiveCard id={idNamespace} marginTop={5} active={active} onClick={(this.handleCardClick)}>
        <Trigger>
          <TreatsTriggerModal />
        </Trigger>
        <H3 color={mainTheme.colors.utility.ink} marginBottom={2}>
          <FormattedMessage id="wallet.walletBalanceCard.flexiPoints.title" />
        </H3>
        <Flex justifyContent="space-between">
          <Flex direction="column" id={id("available-balance", idNamespace)}>
            <Label color={mainTheme.colors.utility.contrast}>
              <FormattedMessage id="wallet.walletBalanceCard.available.header" />
            </Label>
            <H2 color={mainTheme.colors.primary2}>{currency && <FormattedCurrency
              value={availableBalance}
              isCents={false}
              currency={currency} />}</H2>
          </Flex>

          {!!expiredFlexiPoints?.total &&
          <Flex alignItems="flex-start" direction="column">
            <Label color={mainTheme.colors.utility.contrast}>
              <Flex alignItems="center">
                <FormattedMessage id="wallet.availableBalance.info.expire" />
                <Tooltip
                  tooltipChildren={<ExpiringSoonTooltipContent id="expiringFlexiPointsList" expiredFlexiPoints={expiredFlexiPoints} />}>
                  <Icon icon={IconTypes.circleInformation} large marginLeft={0.5} />
                </Tooltip>
              </Flex>
            </Label>
            <Flex>
              <Icon icon={IconTypes.flexiPoints} size={24} marginRight={0.5} />
              <H4 color={mainTheme.colors.primary2}>
                {getNormalisedValue(true, currency, expiredFlexiPoints?.total)}
              </H4>
            </Flex>
          </Flex>
          }
        </Flex>
        <div>
          <Body color={mainTheme.colors.utility.contrast} marginTop={2} marginBottom={2}>
            <FormattedMessage id="wallet.availableBalance.info.text" />
          </Body>
          <ButtonRouterLink
            id={id("browse-perks-button", idNamespace)}
            primary
            stopPropagation
            full
            onClick={walletTracking.clickBrowsePerks}
            load={loadCategories}
            to={ttCategory ? `/categories/${ttCategory.url}` : "/categories"}
            app={apps.PERKS}>
            <FormattedMessage id="wallet.availableBalance.info.button" />
          </ButtonRouterLink>
          {isWellnessHubActive && <ButtonRouterLink
            id={id("browse-wellness-button", idNamespace)}
            stopPropagation
            full
            onClick={walletTracking.clickBrowseWellness}
            load={loadCategories}
            to="/"
            marginTop
            app={apps.WELLNESS}>
            <FormattedMessage id="wallet.availableBalance.info.browseWellnessButton" />
          </ButtonRouterLink>}
        </div>
      </ActiveCard>
    )
  }
}

export default withAuth(TreatTokenWallet)
